import React, { useEffect, useState } from 'react';
import {
  Button,
  colors,
  Select,
  Table,
  TableBody,
  TableHeader,
  TableHeading,
  TableRow,
} from '@insurancemenu/imenu-components-lib';
import { observer } from 'mobx-react-lite';
import { useRootStore } from 'store/rootStore';
import { toJS } from 'mobx';
import clsx from 'clsx';
import { CloudUpload, Share } from '@insurancemenu/imenu-components-lib/dist/icons';
import ActionModal from 'components/common/modals/ActionModal';
import { ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { FileExchangeService } from 'types/exchangeService';
import PdfTableRow from './PdfTableRow';
import { createStyles } from './styles';
import UploadFileModal from '../UploadFileModal';

const ReadyToSendPdfs = observer((): JSX.Element => {
  const classes = createStyles();
  const {
    userStore: { sponsorId: currentSponsorId },
    readyToSendPdfsStore: {
      loading,
      exchangeReadyFiles,
      isModalOpen,
      selectedFile,
      getExchangeReadyFiles,
      closeModal,
      sendFile,
    },
    filesTrackingController: { openUploadModal, showToast, getSponsorOptions, setIsFileUploadReset, isUploadModalOpen },
  } = useRootStore();

  const [sponsorId, setSponsorId] = useState(currentSponsorId);
  const convertedFiles = exchangeReadyFiles.map((exchangeReadyFile) => toJS(exchangeReadyFile)).slice();
  const convertedSelectedFile = selectedFile ? toJS(selectedFile) : null;

  useEffect(() => {
    void getExchangeReadyFiles(sponsorId);
  }, [sponsorId]);

  const sendCurrentFile = async (): Promise<void> => {
    if (convertedSelectedFile?.id) {
      await sendFile(convertedSelectedFile?.id, sponsorId);
      void getExchangeReadyFiles(sponsorId);
    } else {
      showToast(
        'An issue has occurred while attempting to resend the file. Please try again later.',
        ToastColorTypes.WARNING,
      );
    }
    closeModal();
  };

  const filesList = convertedFiles.map((file) => <PdfTableRow key={file.id} file={file} />);

  return (
    <div>
      <div className={classes.actionBar}>
        <Select
          label="Sponsor Id"
          name="sponsorId"
          options={getSponsorOptions()}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>): void => {
            setSponsorId(e.target.value);
          }}
          value={sponsorId}
          width="25rem"
          validationPassed={!!sponsorId}
          validationMessage="Please select sponsor"
        />
        <Button
          onClick={(): void => {
            setIsFileUploadReset(false);
            openUploadModal();
          }}
          size="large"
          color="primary"
          leftIconProp={<CloudUpload />}
          className={classes.uploadButton}
        >
          Upload File
        </Button>
      </div>
      <p className={classes.infoTitle}>
        Total Files:<b className={classes.totalAmount}>{convertedFiles.length}</b>
      </p>
      <div className={classes.tableWrapper}>
        <Table
          className={classes.table}
          loading={loading}
          maxHeight="55rem"
          minHeight="55rem"
          totalElements={convertedFiles.length}
        >
          <TableHeader className={classes.tableHeader}>
            <TableRow>
              <TableHeading className={clsx(classes.tableHeading, classes.longFileInfo)}>File Id</TableHeading>
              <TableHeading className={clsx(classes.tableHeading, classes.longFileInfo)}>Policy Id</TableHeading>
              <TableHeading className={clsx(classes.tableHeading, classes.longFileInfo)}>File Location</TableHeading>
              <TableHeading className={classes.tableHeading}>Creation Date</TableHeading>
              <TableHeading className={classes.tableHeading} />
              <TableHeading className={classes.tableHeading} />
            </TableRow>
          </TableHeader>
          <TableBody className={classes.tableBody}>{filesList}</TableBody>
        </Table>
      </div>
      <ActionModal
        show={isModalOpen}
        title="Send File"
        subtitle={
          <span>
            You are about to send
            <br />
            <b className={classes.selectedFile}>${convertedSelectedFile?.pdfLocation || 'this'}</b> file.
          </span>
        }
        onClose={closeModal}
        actionButtonText="Send File"
        onAction={sendCurrentFile}
        icon={<Share width="4.8rem" color={colors.green[600]} />}
      />
      <UploadFileModal
        show={isUploadModalOpen}
        uploadFileFormat={FileExchangeService.PDF}
        allowMultipleFiles
        sponsorId={sponsorId}
        onUpdate={(sponsor: string): void => {
          setSponsorId(sponsor);
          void getExchangeReadyFiles(sponsor);
        }}
      />
    </div>
  );
});

export default ReadyToSendPdfs;
